import React, { ReactElement, useEffect, useMemo, useRef } from "react";
import { Field } from "@progress/kendo-react-form";
import { Loader } from "@progress/kendo-react-indicators";
import { useLocalization } from '@progress/kendo-react-intl';
import { useQuery } from 'react-query';
import { enMessages } from "../../../../messages/en-US";
import { ReactQueryKeys } from "../../../../Constants/ClientRoutingConstants";
import { FormInput, FormButtonGroup } from "../../../Shared/Components/Forms/FormsComponents";
import { FormDropDownList } from "../../../Assets/Utilities/FormComponents";
import administrationApiService from "../../../Administration/Utilities/AdministrationAPIService";
import { SiteReadDto } from "../../../../Models/Site/SiteReadDto";
import { stripTemplateNameSuffix } from "./assetTemplateDialogUtils";

// Constants - display text and value for template type dropdown
const ASSET_TEMPLATE_TYPES = [
  { text: "HME", value: "HME" },
  { text: "Locomotive", value: "Locomotive" },
  { text: "Fixed Plant", value: "FixedPlant" },
] as const;

function getImageSrc(base64: string | null): string | null {
  if (!base64 || typeof base64 !== "string") return null;
  return base64.startsWith("data:image") ? base64 : `data:image/png;base64,${base64}`;
}

interface Props {
  assetTemplate?: any;
  formRenderProps?: any;
  existingTemplates?: any[];
  onGreasePumpChange?: (value: boolean) => void;
  /** When true (e.g. from Site Setup), hide account field; current user's account is used. */
  hideAccountField?: boolean;
  /** Image preview when copying from template (base64 string) */
  templateImage?: string | null;
  /** Whether the template image is still loading */
  isLoadingTemplateImage?: boolean;
}

export default function AssetTemplateDetailsComponent(props: Props): ReactElement {
  const localizationService = useLocalization();
  
  // Query to fetch sites/accounts (only when account field is visible, e.g. Admin flow)
  const sitesQuery = useQuery<Array<SiteReadDto>>(
    ReactQueryKeys.AllSitesQuery,
    () => administrationApiService.getAllSites(),
    { enabled: !props.hideAccountField }
  );

  // Watch for changes in hasGreasePumps and notify parent
  const { formRenderProps, onGreasePumpChange } = props;
  const previousValueRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (formRenderProps && onGreasePumpChange) {
      const currentValue = formRenderProps.valueGetter('hasGreasePumps') === true;
      if (previousValueRef.current !== currentValue) {
        previousValueRef.current = currentValue;
        onGreasePumpChange(currentValue);
      }
    }
  }, [formRenderProps, onGreasePumpChange]);
  
  // Validators
  const requiredValidator = useMemo(() => (value: any): string | undefined => {
    return value ? undefined : localizationService.toLanguageString("custom.fieldIsRequired", enMessages.custom.fieldIsRequired);
  }, [localizationService]);


  const nameValidator = useMemo(() => (value: any): string | undefined => {
    if (!value || value.trim() === "") {
      return localizationService.toLanguageString("custom.fieldIsRequired", enMessages.custom.fieldIsRequired);
    }
    
    if (value.trim().length < 2) {
      return localizationService.toLanguageString("custom.nameMinLength", enMessages.custom.nameMinLength);
    }
    
    const trimmedValue = value.trim();
    const currentId = props.assetTemplate?.id;
    const normalizedCurrent = stripTemplateNameSuffix(String(props.assetTemplate?.name ?? ""));
    // When EDITING (id exists): unchanged name is never a duplicate. When copying (id undefined), always check.
    if (currentId && normalizedCurrent && trimmedValue.toLowerCase() === normalizedCurrent.toLowerCase()) {
      return undefined;
    }
    
    if (props.existingTemplates) {
      const duplicateTemplate = props.existingTemplates.find((template) => {
        if (currentId && String(template.id) === String(currentId)) return false;
        const existingName = stripTemplateNameSuffix(String(template.name ?? ""));
        return existingName.toLowerCase() === trimmedValue.toLowerCase();
      });
      if (duplicateTemplate) {
        return localizationService.toLanguageString("custom.nameExists", enMessages.custom.nameExists);
      }
    }
    
    return undefined;
  }, [localizationService, props.existingTemplates, props.assetTemplate?.id, props.assetTemplate?.name]);

  const accountValidator = useMemo(() => (value: any): string | undefined => {
    if (value === undefined || value === null) {
      return localizationService.toLanguageString("custom.fieldIsRequired", enMessages.custom.fieldIsRequired);
    }
    if (typeof value === 'object' && value.id !== undefined) {
      return undefined;
    }
    if (typeof value === 'string') {
      return undefined;
    }
    return undefined;
  }, [localizationService]);

  // Prepare account data with "All accounts" option
  const accountData = useMemo(() => [
    { id: "", name: "All accounts" },
    ...(sitesQuery.data || [])
  ], [sitesQuery.data]);

  return (
    <div className="space-y-4">
      <Field
        name="name"
        component={FormInput}
        label={localizationService.toLanguageString("custom.name", enMessages.custom.name) + " *"}
        validator={nameValidator}
        validatorRequired={true}
        style={{ width: "100%" }}
      />
      
      <Field
        name="type"
        component={FormDropDownList}
        label={localizationService.toLanguageString("custom.type", enMessages.custom.type) + " *"}
        data={[...ASSET_TEMPLATE_TYPES]}
        textField="text"
        valueField="value"
        dataItemKey="value"
        validator={requiredValidator}
        validatorRequired={true}
        filterable={false}
        style={{ width: "100%" }}
      />
      
      <Field
        name="hasGreasePumps"
        component={FormButtonGroup}
        label={localizationService.toLanguageString("custom.greasePumpConnectivity", enMessages.custom.greasePumpConnectivity)}
      />

      {(props.templateImage != null || props.isLoadingTemplateImage) && (
        <>
          {props.isLoadingTemplateImage ? (
            <div className="flex justify-center items-center p-4">
              <Loader type="pulsing" />
            </div>
          ) : props.templateImage ? (
            <div className="flex p-4">
              <img
                src={getImageSrc(props.templateImage) ?? undefined}
                alt="Template preview"
                style={{ maxWidth: "150px", objectFit: "contain" }}
              />
            </div>
          ) : null}
        </>
      )}

      {!props.hideAccountField && (
        <Field
          name="account"
          component={FormDropDownList}
          label={localizationService.toLanguageString("custom.account", enMessages.custom.account) + " *"}
          data={accountData}
          textField="name"
          valueField="id"
          defaultValue={accountData[0]}
          validator={accountValidator}
          validatorRequired={true}
          filterable={true}
          clearButton={true}
          style={{ width: "100%" }}
        />
      )}
    </div>
  );
}
